import "@tento-ui/components/transition";
import { LonaWebComponent, html } from "@tento-ui/component";
import { component } from "@tento-ui/component-decorators";
import { Style, css } from "@tento-ui/component-styles";
import { Toast } from "@tento-ui/components/toast/toast";
import { Transition } from "@tento-ui/components/transition";
import { GESTURE_MANAGER } from "@tento-ui/gesture-manager";
import { ResponseError } from "@tento-core/typed-api";
import { LoadingIcon, LonaIcons } from "@tento-ui/ui/icons";
import { Pip } from "@tento-ui/ui/pip";
import { LonaApi } from "../api";
import { Tracing } from "@tento-core/tracing";

@component({
  name: "lona-api-key-page",
})
export class ApiKeyPage extends LonaWebComponent {
  onLinkSuccess: Option<() => void>;
  private $submitTransition: Transition<HTMLButtonElement, HTMLElement> =
    this.hydrate("submit-transition");
  private $submitButton: Option<HTMLButtonElement> = null;

  static async show(props: ApiKeyPage.Props) {
    const $page = ApiKeyPage.make().bind(props);
    const win = await Pip.show($page, {
      margin: "0px",
      theme: "light",
      size: {
        height: 420,
        width: 360,
      },
    });
    $page.onLinkSuccess = () => {
      Toast.info(`${props.integration} API key saved`);
      win?.close();
      props.onSuccess?.();
    };
  }

  bind(props: ApiKeyPage.Props): this {
    if (this.$submitButton) GESTURE_MANAGER.unregister(this.$submitButton);

    this.$("error").style.display = "none";
    this.$<HTMLImageElement>("logo").src = props.imgUrl;
    this.$("title").textContent = props.title;
    const $link = this.$<HTMLAnchorElement>("key-link");
    $link.href = props.keyUrl;
    $link.textContent = props.description;

    this.$submitButton = document.createElement("button");
    this.$submitButton.type = "button";
    this.$submitButton.textContent = "Save Key";

    const $spinner = this.createSpinner();

    this.$submitTransition.bind({
      state: "before",
      $before: this.$submitButton,
      $after: $spinner,
    });

    GESTURE_MANAGER.addPointerEvent(this.$submitButton, {
      onClick: async () => {
        const apiKey = this.$<HTMLInputElement>("api-key").value.trim();
        if (!apiKey) return;

        this.$submitTransition.bindState("after");
        // Body variant not yet in generated types — cast until gen.ts is re-run
        const r = await LonaApi.postResult("/api/user/connections/{source}", {
          path: {
            source: props.source,
          },
          body: {
            apiKey: { apiKey },
          } as unknown as Parameters<typeof LonaApi.postResult<"/api/user/connections/{source}">>[1]["body"],
        });
        r.match({
          ok: () => this.onLinkSuccess?.(),
          err: (e) => {
            this.$submitTransition.bindState("before");
            this.onLinkFailure(props, e);
          },
        });
      },
    });
    return this;
  }

  private createSpinner(): HTMLElement {
    const $spinner = document.createElement("div");
    $spinner.className = "spinner-container";
    const $icon = LonaIcons.make(LoadingIcon, {
      sizePx: 24,
    });
    $spinner.appendChild($icon);
    return $spinner;
  }

  onLinkFailure(props: ApiKeyPage.Props, e: ResponseError) {
    Tracing.err("ui")("api-key-error", e);
    const $error = this.$("error");
    $error.textContent = ResponseError.sentence(
      e,
      "Something went wrong. Please try again.",
    );
    $error.style.display = "block";
  }

  static $styles: Style.Compat[] = [
    css`
      :host {
        font-family: sans-serif;
        display: block;
      }

      #root {
        min-height: calc(420px - 16px);
        width: calc(360px - 16px);
        background: var(--card-background-color, #fff);
        border-radius: 12px;
        margin: 8px;
        padding: 24px;
        box-sizing: border-box;
        display: flex;
        flex-direction: column;
        align-items: center;
        gap: 16px;
        box-shadow: 0 4px 12px rgba(0, 0, 0, 0.08);
      }

      #logo {
        margin-top: 32px;
        max-height: 48px;
        max-width: 80%;
        object-fit: contain;
      }

      #title {
        font-size: 1.25rem;
        text-align: center;
        margin: 0;
        color: var(--text-color, #222);
        margin-bottom: 4px;
      }

      #key-link {
        font-size: 0.85rem;
        color: #007aff;
        text-decoration: none;
        text-align: center;
        margin-bottom: 8px;
      }

      #key-link:hover {
        text-decoration: underline;
      }

      #key-form {
        display: flex;
        flex-direction: column;
        width: 100%;
        gap: 12px;
      }

      #key-form input {
        padding: 10px;
        border: 1px solid #ccc;
        border-radius: 6px;
        font-size: 1rem;
        background: #fafafa;
        font-family: monospace;
      }

      #key-form input:focus {
        outline: none;
        border-color: #007aff;
        background: var(--bg);
      }

      #submit-transition button {
        width: 100%;
        padding: 10px;
        background-color: #007aff;
        color: white;
        border: none;
        border-radius: 6px;
        font-size: 1rem;
        cursor: pointer;
        transition: background 0.2s ease;
      }

      #submit-transition button:hover {
        background-color: #005fcc;
      }

      .spinner-container {
        display: flex;
        justify-content: center;
        align-items: center;
        width: 100%;
        padding: 10px;
      }

      #error {
        display: none;
        margin: 0;
        padding: 8px 12px;
        border-radius: 6px;
        background: #fef2f2;
        color: #991b1b;
        font-size: 0.85rem;
        line-height: 1.4;
        width: 100%;
        box-sizing: border-box;
        text-align: center;
      }
    `,
  ];

  static $html: Option<HTMLTemplateElement> = html`
    <std-col id="root">
      <img id="logo" />
      <h1 id="title" class="h6"></h1>
      <a id="key-link" target="_blank" rel="noopener noreferrer"></a>
      <form id="key-form">
        <input type="password" id="api-key" placeholder="API Key" required />
        <std-transition id="submit-transition"></std-transition>
      </form>
      <p id="error"></p>
    </std-col>
  `;
}

export namespace ApiKeyPage {
  export type Props = {
    integration: string;
    source: string;
    title: string;
    description: string;
    keyUrl: string;
    imgUrl: string;
    onSuccess?: () => void;
  };

  export const GEMINI: Props = {
    integration: "Gemini",
    source: "gemini",
    title: "Add your Gemini API key",
    description: "Get your API key from Google AI Studio",
    keyUrl: "https://aistudio.google.com/apikey",
    imgUrl: "/@/static/img/integrations/gemini-64-icon.svg",
  };

  export const CLAUDE: Props = {
    integration: "Claude",
    source: "claude",
    title: "Add your Claude API key",
    description: "Get your API key from the Anthropic Console",
    keyUrl: "https://platform.claude.com/settings/keys",
    imgUrl: "/@/static/img/integrations/claude-64-icon.svg",
  };

  export const OPENAI: Props = {
    integration: "OpenAI",
    source: "openai",
    title: "Add your OpenAI API key",
    description: "Get your API key from the OpenAI Dashboard",
    keyUrl: "https://platform.openai.com/api-keys",
    imgUrl: "/@/static/img/integrations/openai-64-icon.svg",
  };
}
